/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useRouterStore, RouterStore } from 'mobx-state-router';
import React, { useMemo, useState } from 'react';
import useSWR from 'swr';

import { Colors, Spacing } from 'src/common/ui/base';
import {
  Alert,
  CircleButton,
  LoadingSpinner,
  Page,
  PageTitle,
  SmartTable,
} from 'src/common/ui/components';
import { TEAM_ROLE_OPTIONS } from 'src/components/forms/schemas/definitions';
import RouterLink from 'src/components/general/routerLink';
import { ProvidersQuery } from 'src/generated/gql/graphql';
import { LOAD_PROVIDERS } from 'src/stores/queries/providers';

const useProviders = ({
  first,
  skip,
  orderBy,
}: {
  first: number;
  skip: number;
  orderBy?: string;
}) =>
  useSWR<ProvidersQuery>([
    LOAD_PROVIDERS,
    {
      first,
      skip,
      orderBy,
    },
  ]);

const Avatar: React.FC<{ url?: string }> = ({ url }) =>
  url ? (
    <img
      src={url}
      alt="avatar"
      css={css`
        background-color: ${Colors.CoolGray5};
        border-radius: 50%;
        display: block;
        height: ${Spacing.Unit5}px;
        object-fit: cover;
        width: ${Spacing.Unit5}px;
      `}
    />
  ) : null;

const structureItem = (
  item: NonNullable<ProvidersQuery['items'][number]>,
  routerStore: RouterStore,
) => ({
  id: item.id as string,
  firstName: item.firstName,
  lastName: item.lastName,
  email: item.email,
  teamRole: item.teamRole ? TEAM_ROLE_OPTIONS[item.teamRole] : '',
  avatar: item.avatar?.url ? <Avatar url={item.avatar.url} /> : undefined,
  edit: (
    <CircleButton
      color="secondary"
      icon="edit"
      onClick={event => {
        event.stopPropagation();
        routerStore.goTo('editProvider', {
          params: { id: item.id },
        });
      }}
    />
  ),
  onClick: () =>
    routerStore.goTo('showProvider', {
      params: { id: item.id },
    }),
});

export const ProvidersListPage = () => {
  const routerStore = useRouterStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortKey, setSortKey] = useState<string | null>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { data, error, isLoading } = useProviders({
    first: rowsPerPage,
    skip: (currentPage - 1) * rowsPerPage,
    orderBy: sortKey ? `${sortKey}_${sortDirection.toUpperCase()}` : undefined,
  });

  const parsedData = useMemo(
    () =>
      data?.items
        ?.map(item => (item ? structureItem(item, routerStore) : null))
        ?.filter((item): item is NonNullable<typeof item> => Boolean(item)) || [],
    [data?.items, routerStore],
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1); // Reset to the first page when rows per page changes
  };

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
  };

  return (
    <Page fullWidth>
      <PageTitle
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <span>Providers</span>
        <RouterLink routeName="createProvider">
          <CircleButton icon="add" color="inverted" />
        </RouterLink>
      </PageTitle>

      {error ? (
        <Alert title="Issue loading providers" message={error?.message || ''} error={error} />
      ) : null}

      {isLoading ? (
        <figure
          css={css`
            display: block;
            margin: 0;
            padding: 0;
            position: relative;
          `}
        >
          <LoadingSpinner
            css={css`
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            `}
          />
        </figure>
      ) : null}

      <SmartTable
        css={css`
          background: transparent;
          opacity: ${isLoading ? 0.25 : 1};
        `}
        controlled
        data={parsedData}
        columns={[
          {
            label: 'First',
            key: 'firstName',
            sortable: true,
          },
          {
            label: 'Last',
            key: 'lastName',
            sortable: true,
          },
          {
            label: 'Email',
            key: 'email',
            sortable: true,
          },
          {
            label: 'Role',
            key: 'teamRole',
            sortable: false,
          },
          {
            label: 'Avatar',
            key: 'avatar',
            sortable: false,
          },
          {
            label: 'Edit',
            key: 'edit',
            sortable: false,
          },
        ]}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
        // Unfortunately, the API doesn't return the total number of rows
        totalRows={null}
      />
    </Page>
  );
};
